import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox } from '@saleshandy/icons';
import { SkeletonLoading } from '@saleshandy/design-system';
import { RequestStatus } from '../../../../../../../../shared/enums/request-status';
import { getIsRequestPending } from '../../../../../../../../shared/utils/get-request-status';
import { executeOnRequestStatus } from '../../../../../../../../shared/utils';
import toaster, { Theme } from '../../../../../../../../shared/toaster';
import { StorageUtil } from '../../../../../../../../shared/utils/storage';
import TasksTabHeader from './components';
import TaskList from './components/task-list';
import AddTaskForm from './components/add-task-form';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import { TaskFilterKey, TaskStatusKey } from './utils/enums';
import {
  getInitialFormValues,
  getTaskTypeFromFilter,
  getUpdatedTask,
  shouldAddTaskBackOnUndo,
  shouldRemoveTask,
} from './utils/helper';
import {
  TaskStatusParamsKey,
  TaskAction,
} from '../../../../../../../tasks/utils/enums';
import { SequenceStepType } from '../../../../../../../sequence/enums';
import {
  GetTasksParams,
  UpdateTask,
} from '../../../../../../../tasks/utils/types';
import { getToasterMessage } from '../../../../../../../tasks/components/tasks-content/utils/helper';
import {
  CreateProspectNoteProp,
  UpdateProspectNoteProps,
} from '../../../../../../types';
import NoProspectDetailResultScreen from '../../../no-prospect-detail-result-screen/no-prospect-detail-result-screen';
import { Images } from '../../../../../../../../shared/app-constants';
import { RootState } from '../../../../../../../../store/root-reducer';
import { setLoadedSkeletonForCurrrentProspect } from '../../../../../../prospect-slice';

interface TasksListTabProps {
  sendGetTasksRequest?: (params: GetTasksParams) => void;
  getTasksRequestStatus?: RequestStatus;
  tasks?: any[];
  selectedProspectId?: number;
  sendUpdateTasksRequest?: (payload: UpdateTask) => void;
  taskActionRequestStatus?: RequestStatus;
  taskActionRequestMessage?: string;
  sendGetUndoTasksRequest?: (params: GetTasksParams, taskId: number) => void;
  getUndoTasksRequestStatus?: RequestStatus;
  sendCreateTaskRequest?: (payload: any) => void;
  createTaskRequestStatus?: RequestStatus;
  createTaskRequestMessage?: string;
  createTaskRequestError?: any;
  sendGetTaskAssigneeListRequest?: () => void;
  taskAssigneeList?: any[];
  userId?: number;
  sendUploadAttachmentRequest?: (payload: any) => void;
  uploadAttachmentRequestStatus?: RequestStatus;
  uploadAttachmentRequestError?: any;
  uploadedAttachment?: any;
  sendDeleteAttachmentRequest?: (payload: any) => void;
  deleteAttachmentRequestStatus?: RequestStatus;
  deleteAttachmentRequestError?: any;
  deletedAttachmentId?: number;
  sendImageUploadRequest?: (payload: any) => void;
  resetUploadAttachmentRequest?: () => void;
  resetDeleteAttachmentRequest?: () => void;
  resetCreateTaskRequest?: () => void;
  sendGetShareableTaskerDataRequest?: (hashId: { hashId: string }) => void;
  getShareableTaskerDataRequestStatus?: RequestStatus;
  shareableTaskerData?: any;
  resetShareableTaskerState?: () => void;
  sendGetWhatsappLinkRequest?: (taskId: number) => void;
  getWhatsappLinkRequestStatus?: RequestStatus;
  getWhatsappLinkResponse?: string;
  resetGetWhatsappLinkState?: () => void;
  setShowGlobalRestrictionModal?: (type: SequenceStepType) => void;
  sendCreateProspectNoteRequest?: (payload: CreateProspectNoteProp) => void;
  createProspectNoteRequestStatus?: RequestStatus;
  createProspectNoteRequestMessage?: string;
  createProspectNoteResponse?: any;
  resetCreateProspectNotesListRequest?: () => void;
  sendUpdateProspectNoteRequest?: (payload: UpdateProspectNoteProps) => void;
  updateProspectNoteRequestStatus?: RequestStatus;
  resetUpdateProspectNoteRequest?: () => void;
  initialShowAddTaskForm?: boolean;
  onAddTaskFormClose?: () => void;
  onActivityChange?: (value: boolean) => void;
  onNoteChange?: (value: boolean) => void;
  activeTab?: string;
  fetchTasks?: boolean;
  onTaskChange?: (value: boolean) => void;
  prospectActivityCount?: {
    notes: string | number;
    tasks: number | string;
    emails: string | number;
    sequences: string | number;
  };
  editProspectAcitivityCount?: (payload: any) => void;
  isDemoAccount?: boolean;
}

const TasksListTab: React.FC<TasksListTabProps> = ({
  sendGetTasksRequest,
  getTasksRequestStatus,
  tasks = [],
  selectedProspectId,
  sendUpdateTasksRequest,
  taskActionRequestStatus,
  taskActionRequestMessage,
  sendGetUndoTasksRequest,
  getUndoTasksRequestStatus,
  sendCreateTaskRequest,
  createTaskRequestStatus,
  createTaskRequestMessage,
  createTaskRequestError,
  sendGetTaskAssigneeListRequest,
  taskAssigneeList = [],
  userId,
  sendUploadAttachmentRequest,
  uploadAttachmentRequestStatus,
  uploadAttachmentRequestError,
  uploadedAttachment,
  sendDeleteAttachmentRequest,
  deleteAttachmentRequestStatus,
  deleteAttachmentRequestError,
  deletedAttachmentId,
  sendImageUploadRequest,
  resetUploadAttachmentRequest,
  resetDeleteAttachmentRequest,
  resetCreateTaskRequest,
  sendGetShareableTaskerDataRequest,
  getShareableTaskerDataRequestStatus,
  shareableTaskerData,
  resetShareableTaskerState,
  sendGetWhatsappLinkRequest,
  getWhatsappLinkRequestStatus,
  getWhatsappLinkResponse,
  resetGetWhatsappLinkState,
  setShowGlobalRestrictionModal,
  sendCreateProspectNoteRequest,
  createProspectNoteRequestStatus,
  createProspectNoteRequestMessage,
  createProspectNoteResponse,
  resetCreateProspectNotesListRequest,
  sendUpdateProspectNoteRequest,
  updateProspectNoteRequestStatus,
  resetUpdateProspectNoteRequest,
  initialShowAddTaskForm = false,
  onAddTaskFormClose,
  onActivityChange,
  onNoteChange,
  activeTab,
  fetchTasks,
  onTaskChange,
  prospectActivityCount,
  editProspectAcitivityCount,
  isDemoAccount,
}) => {
  const dispatch = useDispatch();

  const [showAddTaskForm, setShowAddTaskForm] = useState(
    initialShowAddTaskForm,
  );
  const [selectedStatus, setSelectedStatus] = useState<TaskStatusKey>(
    TaskStatusKey.All,
  );
  const [selectedFilter, setSelectedFilter] = useState<TaskFilterKey>(
    TaskFilterKey.All,
  );
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLimit] = useState(20);
  const [localTasks, setLocalTasks] = useState(tasks);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreTasks, setHasMoreTasks] = useState(true);
  const [wasTaskCreatedAsCompleted, setWasTaskCreatedAsCompleted] = useState(
    false,
  );
  const taskActionRef = useRef<TaskAction | null>(null);
  const taskIdRef = useRef<number | null>(null);
  const snoozeDateRef = useRef<Date | null>(null);
  const selectedStatusRef = useRef<TaskStatusKey>(selectedStatus);
  const pageNumberRef = useRef<number>(pageNumber);
  const pageLimitRef = useRef<number>(pageLimit);
  const lastProcessedUrlTaskId = useRef<string | null>(null);
  const isInitialLoadRef = useRef<boolean>(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef<boolean>(false);
  const closeExpandedTasksRef = useRef<(() => void) | null>(null);

  const updateTaskRequestStatus = useSelector(
    (state: RootState) => state.tasks.updateTasksRequest.status,
  );
  const loadedSkeletonForCurrrentProspect = useSelector(
    (state: RootState) => state.prospect?.loadedSkeletonForCurrrentProspect,
  );

  // Helper function to convert TaskStatusKey to TaskStatusParamsKey
  // Helper function to convert TaskStatusKey to TaskStatusParamsKey
  const getTaskStatusParams = (
    status: TaskStatusKey,
  ): TaskStatusParamsKey | undefined => {
    if (status === TaskStatusKey.All) {
      return undefined; // Don't filter by status if "All" is selected
    }
    if (status === TaskStatusKey.Upcoming) {
      return TaskStatusParamsKey.Upcoming;
    }
    if (status === TaskStatusKey.Skipped) {
      return TaskStatusParamsKey.Skipped;
    }
    if (status === TaskStatusKey.Completed) {
      return TaskStatusParamsKey.Completed;
    }
    if (status === TaskStatusKey.Overdue) {
      return TaskStatusParamsKey.Overdue;
    }
    return TaskStatusParamsKey.DueToday;
  };

  // Update refs when values change
  useEffect(() => {
    selectedStatusRef.current = selectedStatus;
  }, [selectedStatus]);

  useEffect(() => {
    pageNumberRef.current = pageNumber;
  }, [pageNumber]);

  useEffect(() => {
    pageLimitRef.current = pageLimit;
  }, [pageLimit]);

  // Check for taskId in URL query params
  const getTaskIdFromUrl = (): string | null => {
    if (typeof window === 'undefined') return null;
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get('taskId');
  };

  // Remove taskId from URL query params
  const removeTaskIdFromUrl = () => {
    if (typeof window === 'undefined') return;
    const queryParams = new URLSearchParams(window?.location?.search);
    if (queryParams?.has('taskId')) {
      queryParams?.delete('taskId');
      const newUrl = queryParams?.toString()
        ? `${window?.location?.pathname}?${queryParams?.toString()}`
        : window?.location?.pathname;
      window?.history?.replaceState(null, '', newUrl);
      // Reset the last processed URL task ID
      lastProcessedUrlTaskId.current = null;
    }
  };

  useEffect(() => {
    if (initialShowAddTaskForm) {
      setShowAddTaskForm(true);
      // Remove taskId from URL when add task form is opened via prop
      removeTaskIdFromUrl();
    } else {
      setShowAddTaskForm(false);
    }
  }, [initialShowAddTaskForm]);

  // Update local tasks when tasks prop changes
  useEffect(() => {
    if (isInitialLoadRef.current || pageNumber === 1) {
      // For initial load or when resetting to page 1, replace tasks
      setLocalTasks(tasks);
      isInitialLoadRef.current = false;
      // Check if there are more tasks based on returned tasks length
      setHasMoreTasks(tasks?.length === pageLimit);
    } else {
      // For subsequent pages, append tasks
      setLocalTasks((prevTasks) => {
        const existingIds = new Set(prevTasks?.map((task) => task.id) || []);
        const newTasks =
          tasks?.filter((task) => !existingIds.has(task.id)) || [];
        const updatedTasks = [...(prevTasks || []), ...newTasks];
        // Check if there are more tasks
        setHasMoreTasks(newTasks.length === pageLimit);
        return updatedTasks;
      });
    }
    setIsLoadingMore(false);
    isLoadingMoreRef.current = false;
  }, [tasks, pageLimit]);

  // Sync ref with isLoadingMore state
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  // Fetch task assignee list when component mounts
  useEffect(() => {
    // if (sendGetTaskAssigneeListRequest && userId) {
    sendGetTaskAssigneeListRequest();
    // }
  }, [sendGetTaskAssigneeListRequest, userId]);

  // Flow 2: When taskId is in URL, call both shareable API and list API
  useEffect(() => {
    const taskId = getTaskIdFromUrl();

    // Only process if taskId exists and is different from last processed one
    if (taskId && taskId !== lastProcessedUrlTaskId.current) {
      lastProcessedUrlTaskId.current = taskId;
      // Reset pagination when viewing a specific task
      setPageNumber(1);
      setHasMoreTasks(true);
      isInitialLoadRef.current = true;

      // Call shareable API first
      if (sendGetShareableTaskerDataRequest) {
        sendGetShareableTaskerDataRequest({ hashId: taskId });
      }

      // Also call list API to get all tasks (without filters to show all tasks)
      if (sendGetTasksRequest) {
        const params: GetTasksParams = {
          status: getTaskStatusParams(selectedStatus),
          pageNumber: 1,
          pageLimit,
          taskType: getTaskTypeFromFilter(selectedFilter),
          prospectId: selectedProspectId,
        };
        sendGetTasksRequest(params);
      }
    } else if (!taskId && lastProcessedUrlTaskId.current) {
      // Reset when taskId is removed from URL
      lastProcessedUrlTaskId.current = null;
      setPageNumber(1);
      setHasMoreTasks(true);
      isInitialLoadRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Only depend on functions, not on filters/status/page to prevent multiple calls
    sendGetShareableTaskerDataRequest,
    sendGetTasksRequest,
  ]);

  // Handle shareable task data response (for URL flow)
  useEffect(() => {
    const taskId = getTaskIdFromUrl();
    if (!taskId) return; // Only handle for URL flow

    executeOnRequestStatus({
      status: getShareableTaskerDataRequestStatus,
      onSuccess: () => {
        // Shareable data will be handled by TaskList component
        if (resetShareableTaskerState) {
          resetShareableTaskerState();
        }
      },
      onFailed: () => {
        if (resetShareableTaskerState) {
          resetShareableTaskerState();
        }
      },
    });
  }, [
    getShareableTaskerDataRequestStatus,
    shareableTaskerData,
    resetShareableTaskerState,
  ]);

  useEffect(() => {
    if (activeTab !== 'tasks') {
      return;
    }

    if (fetchTasks && sendGetTasksRequest && selectedProspectId) {
      // Close expanded tasks when coming from notes tab
      if (closeExpandedTasksRef?.current) {
        closeExpandedTasksRef.current();
      }
      const params: GetTasksParams = {
        status: getTaskStatusParams(selectedStatus),
        pageNumber: 1,
        pageLimit,
        taskType: getTaskTypeFromFilter(selectedFilter),
        prospectId: selectedProspectId,
      };
      sendGetTasksRequest(params);
      onTaskChange?.(false);
      setPageNumber(1);
      setHasMoreTasks(true);
      isInitialLoadRef.current = true;
    }
  }, [
    fetchTasks,
    sendGetTasksRequest,
    selectedProspectId,
    selectedStatus,
    selectedFilter,
    pageLimit,
    onTaskChange,
    activeTab,
  ]);

  // Fetch tasks when status, filter, or prospectId changes (only if no taskId in URL)
  useEffect(() => {
    // const taskId = getTaskIdFromUrl();
    // Don't fetch regular tasks if we're viewing a shareable task (already fetched above)
    // if (taskId) return;

    if (sendGetTasksRequest) {
      // Only set loading more if it's not the first page
      if (pageNumber > 1) {
        setIsLoadingMore(true);
        isLoadingMoreRef.current = true;
      } else if (closeExpandedTasksRef.current) {
        closeExpandedTasksRef.current();
      }
      const params: GetTasksParams = {
        status: getTaskStatusParams(selectedStatus),
        pageNumber,
        pageLimit,
        taskType: getTaskTypeFromFilter(selectedFilter),
        prospectId: selectedProspectId,
      };
      sendGetTasksRequest(params);
    }
  }, [
    selectedStatus,
    selectedFilter,
    pageNumber,
    pageLimit,
    sendGetTasksRequest,
  ]);

  // Handle task action success
  useEffect(() => {
    executeOnRequestStatus({
      status: taskActionRequestStatus,
      onSuccess: () => {
        const taskIdToUse = taskIdRef.current;
        const taskAction = taskActionRef.current;
        const rescheduledDate = snoozeDateRef.current;

        if (taskIdToUse !== null && taskAction !== null) {
          const taskToStore = localTasks?.find(
            (task) => task.id === taskIdToUse,
          );

          if (taskToStore) {
            const originalIndex =
              localTasks?.findIndex((task) => task.id === taskIdToUse) ?? -1;

            const undoDataWithIndex = {
              task: taskToStore,
              originalIndex: originalIndex >= 0 ? originalIndex : 0,
              originalAction: taskAction,
              originalSourceStatus: selectedStatus,
              ...(taskAction === TaskAction.SnoozeTask &&
                snoozeDateRef.current && { snoozeDate: snoozeDateRef.current }),
            };

            // Store in localStorage for undo
            StorageUtil.setItem('undoTask', JSON.stringify(undoDataWithIndex));

            // Determine if task should be removed or updated
            const shouldRemove = shouldRemoveTask(
              selectedStatus,
              taskAction,
              rescheduledDate,
            );

            if (shouldRemove) {
              // Remove the task from local list
              setLocalTasks(
                (currentTasks) =>
                  currentTasks?.filter((task) => task.id !== taskIdToUse) ?? [],
              );
            } else {
              // Update the task in local list
              setLocalTasks((currentTasks) => {
                if (!currentTasks) return currentTasks;
                return currentTasks.map((task) => {
                  if (task.id === taskIdToUse) {
                    return getUpdatedTask(task, taskAction, rescheduledDate);
                  }
                  return task;
                });
              });
            }

            // Get params for undo API
            const params: GetTasksParams = {
              status: getTaskStatusParams(selectedStatus),
              pageNumber,
              pageLimit,
              taskType: getTaskTypeFromFilter(selectedFilter),
            };

            // Show success message with undo button (no undo for retry)
            toaster.success(getToasterMessage(taskAction), {
              theme: Theme.New,
              ...(taskAction !== TaskAction.RetryLinkedInTask && {
                showUndo: true,
                delay: 5000,
                undoAction: () => {
                  const undoData = JSON.parse(StorageUtil.getItem('undoTask'));
                  if (undoData?.task && sendGetUndoTasksRequest) {
                    sendGetUndoTasksRequest(params, undoData.task.id);
                  }
                },
              }),
            });
          }
        }

        // Reset refs
        taskActionRef.current = null;
        taskIdRef.current = null;
        snoozeDateRef.current = null;

        // Remove taskId from URL after action is performed
        removeTaskIdFromUrl();
        onActivityChange?.(true);
      },
      onFailed: () => {
        if (taskActionRequestMessage) {
          toaster.error(taskActionRequestMessage, { theme: Theme.New });
        }
        taskActionRef.current = null;
        taskIdRef.current = null;
        snoozeDateRef.current = null;
      },
    });
  }, [
    taskActionRequestStatus,
    taskActionRequestMessage,
    localTasks,
    selectedStatus,
    onActivityChange,
    selectedFilter,
    pageNumber,
    pageLimit,
    sendGetUndoTasksRequest,
  ]);

  // Handle undo success
  useEffect(() => {
    executeOnRequestStatus({
      status: getUndoTasksRequestStatus,
      onSuccess: () => {
        // Get task from localStorage and restore it to the list
        const undoData = JSON.parse(StorageUtil.getItem('undoTask'));

        if (undoData?.task) {
          // Check if task should be added back based on current status filter
          const shouldAddBack = shouldAddTaskBackOnUndo(
            selectedStatus,
            undoData.originalAction,
            undoData.task,
            undoData.snoozeDate,
          );
          if (!undoData?.snoozeDate) {
            editProspectAcitivityCount({
              ...prospectActivityCount,
              tasks:
                Number(prospectActivityCount?.tasks) >= 0
                  ? (Number(prospectActivityCount?.tasks) || 0) + 1
                  : 1,
            });
          }

          if (shouldAddBack) {
            // Update local state directly - insert the task at its original position
            // The undoData.task contains the original task with original completedAt/skippedAt
            // The action button will be enabled/disabled based on these restored values
            setLocalTasks((currentTasks) => {
              if (!currentTasks) return currentTasks;

              const newData = [...currentTasks];
              const insertIndex = Math.min(
                undoData?.originalIndex,
                newData?.length,
              );
              newData.splice(insertIndex, 0, undoData.task);

              return newData;
            });
          } else if (
            selectedStatus === TaskStatusKey.All ||
            selectedStatus === TaskStatusKey.Upcoming
          ) {
            // For "All" or "Upcoming" filter, if task was updated (not removed), restore original state
            // The undoData.task contains the original task before any updates
            // This includes the original dueAt, completedAt, and skippedAt
            // The action button will be enabled/disabled based on these restored values
            setLocalTasks((currentTasks) => {
              if (!currentTasks) return currentTasks;
              return currentTasks.map((task) => {
                if (task.id === undoData.task.id) {
                  // Restore the original task state from undoData
                  return { ...undoData.task };
                }
                return task;
              });
            });
          }
        }

        // Remove from localStorage after successful undo
        StorageUtil.removeItem('undoTask');
      },
    });
  }, [getUndoTasksRequestStatus, selectedStatus]);

  // Cleanup: Remove taskId from URL when component unmounts (drawer closes)
  useEffect(
    () => () => {
      removeTaskIdFromUrl();
    },
    [],
  );

  const handleAddTaskClick = () => {
    const willShowForm = !showAddTaskForm;
    setShowAddTaskForm(willShowForm);
    // Remove taskId from URL when opening add task form
    if (willShowForm) {
      removeTaskIdFromUrl();
    }
  };

  const handleCancel = () => {
    setShowAddTaskForm(false);
    // Remove taskId from URL when closing add task form
    removeTaskIdFromUrl();
    // Notify parent that form is closed
    if (onAddTaskFormClose) {
      onAddTaskFormClose();
    }
  };

  // Handle create task success
  useEffect(() => {
    executeOnRequestStatus({
      status: createTaskRequestStatus,
      onSuccess: () => {
        // if (createTaskRequestMessage) {
        //   toaster.success(createTaskRequestMessage, {
        //     theme: Theme.New,
        //   });
        // }
        setShowAddTaskForm(false);
        // Remove taskId from URL after creating task
        removeTaskIdFromUrl();
        // Notify parent that form is closed
        if (onAddTaskFormClose) {
          onAddTaskFormClose();
        }
        // Reset to first page to show the newly created task
        setPageNumber(1);
        // Refresh tasks list
        if (sendGetTasksRequest) {
          const params: GetTasksParams = {
            status: getTaskStatusParams(selectedStatus),
            pageNumber: 1, // Always fetch first page after creating a task
            pageLimit,
            taskType: getTaskTypeFromFilter(selectedFilter),
            prospectId: selectedProspectId,
          };
          sendGetTasksRequest(params);
        }
        // Only increment task count if task was not created as completed
        if (!wasTaskCreatedAsCompleted) {
          editProspectAcitivityCount({
            ...prospectActivityCount,
            tasks:
              Number(prospectActivityCount?.tasks) >= 0
                ? Number(prospectActivityCount?.tasks) + 1
                : 1,
          });
        }
        // Reset the flag after handling
        setWasTaskCreatedAsCompleted(false);
        // Reset create task request state
        if (resetCreateTaskRequest) {
          resetCreateTaskRequest();
        }
        onActivityChange?.(true);
      },
      onFailed: () => {
        // if (createTaskRequestError) {
        //   toaster.error(
        //     createTaskRequestError?.message || 'Failed to create task',
        //     { theme: Theme.New },
        //   );
        // }
        // Reset create task request state even on failure
        if (resetCreateTaskRequest) {
          resetCreateTaskRequest();
        }
      },
    });
  }, [
    createTaskRequestStatus,
    createTaskRequestMessage,
    createTaskRequestError,
    sendGetTasksRequest,
    resetCreateTaskRequest,
    selectedStatus,
    onActivityChange,
    selectedFilter,
    pageLimit,
  ]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getTasksRequestStatus,
      onSuccess: () => {
        dispatch(setLoadedSkeletonForCurrrentProspect({ tasks: true }));
      },
    });
  }, [getTasksRequestStatus]);

  const handleSubmit = (formData: any) => {
    // Track if task is being created as completed (taskAction: 0)
    setWasTaskCreatedAsCompleted(formData.taskAction === 0);
    // if (sendCreateTaskRequest) {
    sendCreateTaskRequest(formData);
    // }
  };

  const handleStatusChange = (status: TaskStatusKey) => {
    dispatch(setLoadedSkeletonForCurrrentProspect({ tasks: false }));
    // Only reset state if status actually changed
    if (status === selectedStatus) {
      return;
    }
    setSelectedStatus(status);
    setPageNumber(1); // Reset to first page when status changes
    setHasMoreTasks(true); // Reset hasMoreTasks when status changes
    setIsLoadingMore(false); // Reset loading more state
    isLoadingMoreRef.current = false; // Reset ref
    setLocalTasks([]); // Clear tasks immediately to show skeleton
    isInitialLoadRef.current = true; // Mark as initial load
    setShowAddTaskForm(false); // Close add task form when status changes
    // Remove taskId from URL when status changes
    removeTaskIdFromUrl();
  };

  const handleFilterChange = (filter: TaskFilterKey) => {
    dispatch(setLoadedSkeletonForCurrrentProspect({ tasks: false }));
    // Only reset state if filter actually changed
    if (filter === selectedFilter) {
      return;
    }
    setSelectedFilter(filter);
    setPageNumber(1); // Reset to first page when filter changes
    setHasMoreTasks(true); // Reset hasMoreTasks when filter changes
    setIsLoadingMore(false); // Reset loading more state
    isLoadingMoreRef.current = false; // Reset ref
    setLocalTasks([]); // Clear tasks immediately to show skeleton
    isInitialLoadRef.current = true; // Mark as initial load
    setShowAddTaskForm(false); // Close add task form when filter changes
    // Remove taskId from URL when filter changes
    removeTaskIdFromUrl();
  };

  // Handle load more for infinite scroll
  const handleLoadMore = () => {
    // Prevent loading more if main request is pending or already loading more
    // Use ref for immediate check to prevent duplicate rapid calls
    if (
      !getIsRequestPending(getTasksRequestStatus) &&
      !isLoadingMore &&
      !isLoadingMoreRef.current &&
      hasMoreTasks
    ) {
      isLoadingMoreRef.current = true; // Set immediately to prevent duplicate calls
      setPageNumber((prev) => prev + 1);
    }
  };

  // Handle task actions (skip, snooze, complete)
  const handleTaskAction = (
    taskId: number,
    action: TaskAction,
    rescheduledDate?: Date,
    callOutcome?: string,
  ) => {
    if (!sendUpdateTasksRequest) return;

    taskActionRef.current = action;
    taskIdRef.current = taskId;
    if (rescheduledDate) {
      snoozeDateRef.current = rescheduledDate;
    }

    const payload: UpdateTask = {
      taskIds: [taskId],
      taskAction: action,
      ...(rescheduledDate && { rescheduledDate: rescheduledDate.toString() }),
      ...(callOutcome && { callOutcome }),
    };

    sendUpdateTasksRequest(payload);
  };

  const taskIdFromUrl = getTaskIdFromUrl();
  const isLoading =
    getIsRequestPending(getTasksRequestStatus) ||
    (taskIdFromUrl && getIsRequestPending(getShareableTaskerDataRequestStatus));
  const isActionPending = getIsRequestPending(taskActionRequestStatus);

  // Get initial form values based on selected filter
  const initialFormValues = getInitialFormValues(selectedFilter);

  return (
    <div className="tasks-list-tab" ref={scrollContainerRef}>
      <TasksTabHeader
        isLoading={true}
        selectedStatus={selectedStatus}
        onStatusChange={handleStatusChange}
        selectedFilter={selectedFilter}
        onFilterChange={handleFilterChange}
      />
      {selectedFilter !== TaskFilterKey.LinkedInAutomation &&
        (isLoading &&
        !loadedSkeletonForCurrrentProspect?.tasks &&
        !isLoadingMore &&
        !showAddTaskForm ? (
          <div className="add-task cursor-pointer">
            <SkeletonLoading width={16} height={16} />

            <SkeletonLoading
              width={69}
              height={14}
              style={{ borderRadius: '4px' }}
            />
          </div>
        ) : (
          !showAddTaskForm && (
            <div
              className="add-task cursor-pointer"
              {...accessibleOnClick(handleAddTaskClick)}
            >
              <Checkbox width={16} height={16} color="#6b7280" />
              <span>Add task</span>
            </div>
          )
        ))}
      {showAddTaskForm && (
        <div className="add-task-form-wrapper">
          <AddTaskForm
            key={`add-task-form-${selectedFilter}`}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            taskAssigneeList={taskAssigneeList}
            userId={userId}
            defaultStepType={initialFormValues.defaultStepType}
            initialStepType={initialFormValues.initialStepType}
            isSubmitLoading={getIsRequestPending(createTaskRequestStatus)}
            selectedProspectId={selectedProspectId}
            sendUploadAttachmentRequest={sendUploadAttachmentRequest}
            uploadAttachmentRequestStatus={uploadAttachmentRequestStatus}
            uploadAttachmentRequestError={uploadAttachmentRequestError}
            uploadedAttachment={uploadedAttachment}
            sendDeleteAttachmentRequest={sendDeleteAttachmentRequest}
            deleteAttachmentRequestStatus={deleteAttachmentRequestStatus}
            deleteAttachmentRequestError={deleteAttachmentRequestError}
            deletedAttachmentId={deletedAttachmentId}
            sendImageUploadRequest={sendImageUploadRequest}
            resetUploadAttachmentRequest={resetUploadAttachmentRequest}
            resetDeleteAttachmentRequest={resetDeleteAttachmentRequest}
            setShowGlobalRestrictionModal={setShowGlobalRestrictionModal}
          />
        </div>
      )}
      {localTasks.length === 0 &&
      !isLoading &&
      !isLoadingMore &&
      !showAddTaskForm ? (
        <NoProspectDetailResultScreen
          title="No Tasks Assigned"
          description="Stay organized create a task to follow up or take the next step."
          imageSrc={Images.NoProspectTask}
        />
      ) : (
        <div className="task-list-scroll-container">
          <TaskList
            key={`task-list-${selectedFilter}-${selectedStatus}`}
            tasks={localTasks}
            isLoading={isLoading}
            onTaskAction={handleTaskAction}
            isActionPending={isActionPending}
            sendGetShareableTaskerDataRequest={
              sendGetShareableTaskerDataRequest
            }
            getShareableTaskerDataRequestStatus={
              getShareableTaskerDataRequestStatus
            }
            shareableTaskerData={shareableTaskerData}
            resetShareableTaskerState={resetShareableTaskerState}
            taskIdFromUrl={taskIdFromUrl}
            getWhatsappLinkRequest={sendGetWhatsappLinkRequest}
            getWhatsappLinkRequestStatus={getWhatsappLinkRequestStatus}
            getWhatsappLinkResponse={getWhatsappLinkResponse}
            resetGetWhatsappLinkState={resetGetWhatsappLinkState}
            selectedProspectId={selectedProspectId}
            createProspectNote={sendCreateProspectNoteRequest}
            createProspectNoteRequestStatus={createProspectNoteRequestStatus}
            createProspectNoteRequestMessage={createProspectNoteRequestMessage}
            createProspectNoteResponse={createProspectNoteResponse}
            resetCreateProspectNotesListRequests={
              resetCreateProspectNotesListRequest
            }
            updateProspectNote={sendUpdateProspectNoteRequest}
            updateProspectNoteRequestStatus={updateProspectNoteRequestStatus}
            resetUpdateProspectNoteRequests={resetUpdateProspectNoteRequest}
            handleSendAttachmentUploadRequest={sendUploadAttachmentRequest}
            handleSendDeleteAttachmentRequest={sendDeleteAttachmentRequest}
            handleResetUploadAttachmentRequest={resetUploadAttachmentRequest}
            handleResetDeleteAttachmentRequest={resetDeleteAttachmentRequest}
            uploadAttachmentRequestStatus={uploadAttachmentRequestStatus}
            uploadAttachmentRequestError={uploadAttachmentRequestError}
            uploadedAttachment={uploadedAttachment}
            deleteAttachmentRequestStatus={deleteAttachmentRequestStatus}
            deleteAttachmentRequestError={deleteAttachmentRequestError}
            deletedAttachmentId={deletedAttachmentId}
            hasMoreTasks={hasMoreTasks}
            onLoadMore={handleLoadMore}
            isLoadingMore={isLoadingMore}
            scrollContainerRef={scrollContainerRef}
            updateTaskRequestStatus={updateTaskRequestStatus}
            selectedStatus={selectedStatus}
            removeTaskIdFromUrl={removeTaskIdFromUrl}
            onActivityChange={onActivityChange}
            onNoteChange={onNoteChange}
            activeTab={activeTab}
            onCloseExpandedTasks={closeExpandedTasksRef}
            prospectActivityCount={prospectActivityCount}
            editProspectAcitivityCount={editProspectAcitivityCount}
            isTaskTabSkeletonLoaded={loadedSkeletonForCurrrentProspect?.tasks}
            isDemoAccount={isDemoAccount}
            // setShowGlobalRestrictionModal={setShowGlobalRestrictionModal}
          />
        </div>
      )}
    </div>
  );
};

export default TasksListTab;
